import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, Can, CustomConfirm, DetailsCard } from "components";
import { DBBCreateContractModal } from "components/DBB/components/DBBCreateContractModal";
import { SEASONS, STATUS_IDENTIFIERS } from "models";
import { TruckTyres } from "modules/CarDetails/components";
import { DBBLabelsModal } from "modules/CarDetails/components/Tyres/components";
import { convertCarTyresToDBBLabels } from "modules/CarDetails/components/Tyres/components/DBBLabelsModal/utils";
import "modules/CarDetails/components/Tyres/Tyres.scss";
import { groupTruckTyres, useCarLocation, useCustomerInfoQuery, useDeleteTyresMutation } from "modules/CarDetails/hooks";
import { getTyreSeasonIcon } from "utils/common";

import type { SectionMenu } from "components";
import type { Car, DealerLocation, Tyre } from "models";
import type { DBBLabel } from "modules/CarDetails/components/Tyres/components/DBBLabelsModal/utils";
import type { Dispatch, SetStateAction } from "react";

type TyresProps = {
  car: Car;
  appointment_status_identifier?: STATUS_IDENTIFIERS;
  appointment_id?: number;
  canDeleteStorage?: boolean;
  sectionMenu?: SectionMenu;
  isExpanded?: boolean;
  showCard?: boolean;
};

const getTyreColour = (profile: number | null, season: SEASONS, selectedLocation?: DealerLocation) => {
  if (profile === null || !selectedLocation) return "";

  const getColour = (thresholdN: number, thresholdA: number) => {
    if (profile < thresholdN) return "red";
    if (profile < thresholdA) return "orange";
    return "green";
  };

  switch (season) {
    case SEASONS.AllSeason:
      return getColour(selectedLocation.all_season_n_threshold, selectedLocation.all_season_a_threshold);
    case SEASONS.Winter:
      return getColour(selectedLocation.winter_n_threshold, selectedLocation.winter_a_threshold);
    case SEASONS.Summer:
      return getColour(selectedLocation.summer_n_threshold, selectedLocation.summer_a_threshold);
    default:
      return "";
  }
};

const renderTyreHeader = (title: string) => {
  return (
    <div className="header">
      <h4>{title}</h4>
    </div>
  );
};

const renderLeftTyreDetails = (profile: number | null, tyre?: Tyre, carLocation?: DealerLocation) => {
  if (!tyre) return null;

  const { manufacturer, season, description, width, height, size, speed, carry_capacity } = tyre;
  const fullDescription = !description.includes(width + "/" + height + "R" + size) ? `${description} ${width}/${height}R${size} ${carry_capacity}${speed}` : description;

  return (
    <div>
      <div className={`flex-row small-gap tyre-details-font start`}>
        <div className={`tyre-indicator ${getTyreColour(profile, season, carLocation)}`}>
          <p>L</p>
        </div>
        <p>{`${(profile ?? 0).toFixed(2)} mm`}</p>
      </div>
      <div className="tyre-details tyre-details-font">
        <p>
          {manufacturer}
          <span>
            <Icon className={getTyreSeasonIcon(season)} />
          </span>
        </p>
        <p>{fullDescription}</p>
      </div>
    </div>
  );
};

const renderRightTyreDetails = (profile: number | null, tyre?: Tyre, carLocation?: DealerLocation) => {
  if (!tyre) return null;

  const { manufacturer, description, season, width, height, size, speed, carry_capacity } = tyre;
  const fullDescription = !description.includes(width + "/" + height + "R" + size) ? `${description} ${width}/${height}R${size} ${carry_capacity}${speed}` : description;

  return (
    <div>
      <div className="flex-row small-gap end tyre-details-font">
        <p>{`${Number(profile).toFixed(2)} mm`}</p>
        <div className={`tyre-indicator ${getTyreColour(profile, season, carLocation)}`}>
          <p>R</p>
        </div>
      </div>
      <div className="tyre-details tyre-details-font right">
        <p>
          <span>
            <Icon className={getTyreSeasonIcon(season)} />
          </span>
          {manufacturer}
        </p>
        <p>{fullDescription}</p>
      </div>
    </div>
  );
};

type DBBButtonProps = {
  car: Car;
  appointmentID?: number;
  openPortalDBB: (appointmentId?: number) => Promise<void>;
  setIsDBBCreateContractModalOpen: Dispatch<SetStateAction<boolean>>;
};

const DBBButton = ({ car, appointmentID, openPortalDBB, setIsDBBCreateContractModalOpen }: DBBButtonProps) => {
  const { t } = useTranslation();
  const carLocation = useCarLocation(car);

  if (!carLocation?.dbb_enabled) return null;

  if (car.has_dbb)
    return (
      <Button className="light" icon disabled={!appointmentID} onClick={() => openPortalDBB(appointmentID)}>
        <span className="Tyres_DBB-label">DBB</span>
        <Icon className="arrow up right from square" color="green" />
      </Button>
    );

  return (
    <Button className="light" onClick={() => setIsDBBCreateContractModalOpen(true)}>
      <span className="Tyres_Create-DBB-contract-label"> {t("v8_create_dbb_contract", "Create DBB contract")}</span>
      <Icon className="plus" />
    </Button>
  );
};

export const hasTyres = (car?: Car) => !!car?.on_car_tyres || !!car?.in_storage_tyres;
export const Tyres = ({ car, appointment_status_identifier, appointment_id, canDeleteStorage, sectionMenu, isExpanded, showCard }: TyresProps) => {
  const { t } = useTranslation();

  const [DBBLabelModalData, setDBBLabelModalData] = useState<DBBLabel[]>([]);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isDBBCreateContractModalOpen, setIsDBBCreateContractModalOpen] = useState(false);

  const { mutate: deleteTyreMutation, isPending: isDeleting, openPortalDBB } = useDeleteTyresMutation();
  const { data: customers } = useCustomerInfoQuery(appointment_id);
  const carLocation = useCarLocation(car);

  const { on_car_tyres, in_storage_tyres } = car;
  const isDeleteButtonDisabled = appointment_status_identifier === STATUS_IDENTIFIERS.CarCheckStartedStatus;

  async function handleTyreDelete() {
    if (!car.has_dbb) return setIsOpenDeleteModal(true);
    openPortalDBB(appointment_id);
  }

  const tyreSets = (
    <div>
      {on_car_tyres && (
        <div className="Tyres">
          <div className="delete-tyre-title-wrapper">
            <h4>{t("v8_on_car", "ON CAR").toUpperCase()}</h4>
            <Button className="light" onClick={() => setDBBLabelModalData(convertCarTyresToDBBLabels(on_car_tyres))}>
              <p>{t("v8_tyre_labels", "Tyre Labels")}</p>
            </Button>
          </div>
          <div className="flex-row">
            <div className="tyre-container">
              {renderTyreHeader(t("v8_front_tyres", "Front tyres"))}
              <div className="flex-row padding">
                {renderLeftTyreDetails(on_car_tyres.fl_profile, on_car_tyres.front_left_tyre, carLocation)}
                {renderRightTyreDetails(on_car_tyres.fr_profile, on_car_tyres.front_right_tyre, carLocation)}
              </div>
            </div>

            <div className="tyre-container">
              {renderTyreHeader(t("v8_rear_tyres", "Rear tyres"))}
              <div className="flex-row padding">
                {renderLeftTyreDetails(on_car_tyres.rl_profile, on_car_tyres.rear_left_tyre, carLocation)}
                {renderRightTyreDetails(on_car_tyres.rr_profile, on_car_tyres.rear_right_tyre, carLocation)}
              </div>
            </div>
          </div>
        </div>
      )}

      {in_storage_tyres && (
        <div className="Tyres">
          <div className="delete-tyre-title-wrapper">
            <div className="delete-tyre-title-wrapper__inStorage">
              <h4 className="delete-tyre-title-wrapper__h4">{t("v8_in_storage", "In Storage").toUpperCase()}</h4>
              {in_storage_tyres.dbb_location_name && (
                <>
                  <span className="delete-tyre-title-wrapper__storage">{`${t("v8_storage_location", "Storage location")}: `}</span>
                  <span className="delete-tyre-title-wrapper__locationName">{in_storage_tyres.dbb_location_name}</span>
                </>
              )}
            </div>
            <div className="delete-tyre-title-buttons">
              <Button className="light" onClick={() => setDBBLabelModalData(convertCarTyresToDBBLabels(car.in_storage_tyres))}>
                <p>{t("v8_tyre_labels", "Tyre Labels")}</p>
              </Button>
              {canDeleteStorage && (
                <Can I="delete" the="tyres-storage-set">
                  <Button className="delete-tyre-btn light" disabled={isDeleteButtonDisabled} onClick={handleTyreDelete} type="button">
                    <Icon className="trash red" />
                  </Button>
                </Can>
              )}
            </div>
          </div>
          <div className="flex-row">
            <div className="tyre-container">
              {renderTyreHeader(t("v8_front_tyres", "Front tyres"))}
              <div className="flex-row padding">
                {renderLeftTyreDetails(in_storage_tyres.fl_profile, in_storage_tyres.front_left_tyre, carLocation)}
                {renderRightTyreDetails(in_storage_tyres.fr_profile, in_storage_tyres.front_right_tyre, carLocation)}
              </div>
            </div>

            <div className="tyre-container">
              {renderTyreHeader(t("v8_rear_tyres", "Rear tyres"))}
              <div className="flex-row padding">
                {renderLeftTyreDetails(in_storage_tyres.rl_profile, in_storage_tyres.rear_left_tyre, carLocation)}
                {renderRightTyreDetails(in_storage_tyres.rr_profile, in_storage_tyres.rear_right_tyre, carLocation)}
              </div>
            </div>
          </div>
        </div>
      )}

      <TruckTyres car={groupTruckTyres(car)} />
    </div>
  );

  return (
    <>
      {showCard && sectionMenu ? (
        <DetailsCard
          isExpanded={isExpanded}
          sectionMenuId={sectionMenu.id}
          icon={sectionMenu.icon}
          title={sectionMenu.title}
          rightContent={
            <div className="full end">
              {<DBBButton appointmentID={appointment_id} car={car} openPortalDBB={openPortalDBB} setIsDBBCreateContractModalOpen={setIsDBBCreateContractModalOpen} />}
            </div>
          }
        >
          {tyreSets}
        </DetailsCard>
      ) : (
        <>
          <div className="Tyres_DBB">
            {<DBBButton appointmentID={appointment_id} car={car} openPortalDBB={openPortalDBB} setIsDBBCreateContractModalOpen={setIsDBBCreateContractModalOpen} />}
          </div>
          {tyreSets}
        </>
      )}
      <DBBLabelsModal open={DBBLabelModalData.length > 0} onClose={() => setDBBLabelModalData([])} tyreLabels={DBBLabelModalData} car={car} location={carLocation} />
      <CustomConfirm
        type={CUSTOM_CONFIRM_TYPES.Danger}
        isOpen={isOpenDeleteModal}
        isLoading={isDeleting}
        handleCancel={() => setIsOpenDeleteModal(false)}
        handleConfirm={() => {
          setIsOpenDeleteModal(false);
          deleteTyreMutation(car.id);
        }}
        confirmMsg={t("v8_delete_documentation_confirm_message", "Are you sure that you want to delete this? You can't undo this action.")}
      />
      <DBBCreateContractModal car={car} customers={customers} open={isDBBCreateContractModalOpen} onClose={() => setIsDBBCreateContractModalOpen(false)} />
    </>
  );
};
